import { lockBodyScroll } from "../utils/scrollLock";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { registerDialogHost } from "../utils/dialog";
import { useLanguage } from "../context/LanguageContext";
import { useFocusTrap } from "../utils/focusTrap";

// Renders whatever confirmDialog / alertDialog / promptDialog asks for — see
// utils/dialog.js. One at a time; a second request made while one is open
// waits in line rather than replacing it.
let dialogIdCounter = 0;

export default function DialogHost() {
  const { t } = useLanguage();
  const [queue, setQueue] = useState([]); // [{ id, opts, resolve }]
  const [inputValue, setInputValue] = useState("");
  const current = queue[0];
  const cardRef = useRef(null);
  // Keyed on the dialog's own id, not just a boolean — otherwise the trap's
  // effect (which only re-runs when its dependencies change) wouldn't know
  // to re-focus/re-trap when the queue silently advances from one dialog
  // straight to the next (`!!current` stays true the whole time).
  //
  // getInitialFocus is required here, not just the trap's default "first
  // focusable in DOM order" fallback: when dialog B is queued right behind
  // dialog A, B's native `autoFocus` DOES correctly target the right button
  // during React's commit — but the trap's own cleanup-for-A-then-setup-
  // for-B sequence runs in the very next (passive-effect) phase and moves
  // focus again, landing on whatever is first in DOM order (always Cancel)
  // rather than respecting what autoFocus just set. Mirroring the same
  // kind/danger logic the JSX below uses for `autoFocus` keeps the two in
  // sync regardless of how focus bounced in between.
  useFocusTrap(cardRef, current?.id ?? false, {
    getInitialFocus: () => {
      if (!current) return null;
      const { kind, danger } = current.opts;
      if (kind === "confirm" && danger) return cardRef.current?.querySelector(".dlg-btn-secondary");
      if (kind === "prompt") return cardRef.current?.querySelector(".dlg-input");
      return cardRef.current?.querySelector('[type="submit"]');
    }
  });

  const queueRef = useRef(queue);
  queueRef.current = queue;

  useEffect(() => {
    const unregister = registerDialogHost(
      opts => new Promise(resolve => setQueue(q => [...q, { id: ++dialogIdCounter, opts, resolve }]))
    );
    return () => {
      unregister();
      // App.js's maintenance-mode/profile-incomplete gates can unmount this
      // component entirely (a whole different screen replaces it) while a
      // dialog is still awaiting a response — without this, that caller's
      // promise was created against this instance's own setQueue closure
      // and would otherwise never resolve, hanging forever instead of
      // falling back to window.confirm the way dialog.js does when no
      // host was ever registered at all.
      queueRef.current.forEach(item => item.resolve(cancelValue(item.opts.kind)));
    };
  }, []);

  // A dialog belongs to the page it was opened on — the browser's Back button
  // (or any other navigation) would otherwise leave it hanging over a page it
  // has nothing to do with. Closing counts as cancelling, so a pending delete
  // or block is dropped rather than carried out.
  const { pathname } = useLocation();
  const lastPathname = useRef(pathname);
  useEffect(() => {
    if (lastPathname.current === pathname) return;
    lastPathname.current = pathname;
    if (queueRef.current.length === 0) return;
    queueRef.current.forEach(item => item.resolve(cancelValue(item.opts.kind)));
    setQueue([]);
  }, [pathname]);

  useEffect(() => {
    if (current?.opts.kind === "prompt") setInputValue(current.opts.defaultValue || "");
  }, [current]);

  function finish(result) {
    current.resolve(result);
    // Identity check so a double-tap can't also drop the dialog queued behind.
    setQueue(q => (q[0] === current ? q.slice(1) : q));
  }

  function cancel() {
    finish(cancelValue(current.opts.kind));
  }

  function confirm() {
    const kind = current.opts.kind;
    finish(kind === "prompt" ? inputValue : kind === "alert" ? undefined : true);
  }

  useEffect(() => {
    if (!current) return;
    const unlockScroll = lockBodyScroll();
    function onKeyDown(e) {
      if (e.key === "Escape") cancel();
    }
    window.addEventListener("keydown", onKeyDown);
    return () => {
      unlockScroll();
      window.removeEventListener("keydown", onKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current]);

  if (!current) return null;

  const { kind, title, message, confirmText, cancelText, danger, maxLength, placeholder } =
    current.opts;

  return createPortal(
    <div className="dlg-backdrop" onClick={cancel}>
      <style>{css}</style>
      <form
        // Keyed on the dialog's own identity, not just present because a
        // list needs one — two consecutive queued dialogs (e.g. a non-danger
        // confirm immediately followed by a danger: true one) would
        // otherwise reuse the identical DOM node, since the form's structure
        // doesn't change, and React only applies `autoFocus` on mount. That
        // silently left focus sitting on the previous dialog's Confirm
        // button even though the new one's `autoFocus` says Cancel should
        // have it — a real risk for whichever dialog happens to queue behind
        // a danger one. The key forces a fresh mount (and a fresh
        // useFocusTrap run) on every queue transition.
        key={current.id}
        className="dlg-card"
        ref={cardRef}
        tabIndex={-1}
        role={kind === "alert" ? "alertdialog" : "dialog"}
        aria-modal="true"
        aria-label={title || "GatherYou"}
        onClick={e => e.stopPropagation()}
        onSubmit={e => {
          e.preventDefault();
          confirm();
        }}
      >
        <div className="dlg-brand">
          <img src="/brand/logo-mark.png" alt="" width={26} height={26} />
          <span>GatherYou</span>
        </div>

        {title && <div className="dlg-title">{title}</div>}
        <p className="dlg-message">{message}</p>

        {kind === "prompt" && (
          <input
            className="virsa-input dlg-input"
            autoFocus
            value={inputValue}
            maxLength={maxLength}
            placeholder={placeholder}
            onChange={e => setInputValue(e.target.value)}
          />
        )}

        <div className="dlg-actions">
          {kind !== "alert" && (
            <button
              type="button"
              className="dlg-btn dlg-btn-secondary"
              autoFocus={kind === "confirm" && danger}
              onClick={cancel}
            >
              {cancelText || t("common.cancel")}
            </button>
          )}
          <button
            type="submit"
            className={`dlg-btn ${danger ? "dlg-btn-danger" : "dlg-btn-primary"}`}
            autoFocus={kind === "alert" || (kind === "confirm" && !danger)}
          >
            {confirmText || "OK"}
          </button>
        </div>
      </form>
    </div>,
    document.body
  );
}

function cancelValue(kind) {
  return kind === "prompt" ? null : kind === "alert" ? undefined : false;
}

const css = `
  .dlg-backdrop {
    position: fixed;
    inset: 0;
    z-index: 3000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    background: rgba(20, 14, 8, 0.5);
    animation: dlgFade 0.15s ease;
  }
  .dlg-card {
    width: 100%;
    max-width: 340px;
    padding: 22px 20px 18px;
    background: var(--bg-surface);
    border: 1px solid var(--border-medium);
    border-radius: 18px;
    box-shadow: 0 16px 40px rgba(0, 0, 0, 0.3);
    animation: dlgRise 0.18s ease;
  }
  .dlg-brand {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 14px;
    font-family: 'Fraunces', serif;
    font-style: italic;
    font-size: 19px;
    font-weight: 600;
    color: var(--accent-primary);
  }
  .dlg-brand img { border-radius: 7px; }
  .dlg-title {
    margin-bottom: 6px;
    font-family: 'Fraunces', serif;
    font-size: 19px;
    font-weight: 600;
    color: var(--text-heading);
  }
  .dlg-message {
    margin: 0 0 18px;
    font-size: 14.5px;
    line-height: 1.55;
    color: var(--text-body);
    white-space: pre-wrap;
    word-break: break-word;
  }
  .dlg-input { margin-bottom: 18px; }
  .dlg-actions { display: flex; gap: 10px; }
  .dlg-btn {
    flex: 1;
    padding: 12px 14px;
    border-radius: 10px;
    font-size: 14.5px;
    font-weight: 700;
    cursor: pointer;
  }
  .dlg-btn-secondary {
    background: transparent;
    border: 1.5px solid var(--border-input);
    color: var(--text-secondary);
  }
  .dlg-btn-primary {
    border: none;
    background: var(--accent-primary);
    color: var(--accent-contrast);
  }
  .dlg-btn-danger {
    border: none;
    background: var(--accent-danger);
    color: #fff;
  }
  .dlg-btn:hover { filter: brightness(1.06); }
  @keyframes dlgFade { from { opacity: 0; } to { opacity: 1; } }
  @keyframes dlgRise {
    from { opacity: 0; transform: translateY(12px) scale(0.98); }
    to { opacity: 1; transform: translateY(0) scale(1); }
  }
`;
